<script setup>
/**
 * Lucky Draw - Pre Draw. Mirrors Vue2's PreDraw.vue: a 3-step wizard
 * (New/Existing draw -> upload participant + prize lists -> winners),
 * backed by the real /api/luckydraw endpoints.
 */
import { ref, computed, onMounted } from "vue";
import {
  fetchPreDraws,
  uploadPreDraw,
  resetLuckyDraw,
  preDrawExportUrl,
  luckyDrawPickerUrl,
} from "./api";

const step = ref(1);
const loading = ref(false);
const error = ref("");

const drawSelection = ref("new");
const drawName = ref("");
const drawDid = ref("");
const existingDraws = ref([]);

const participantsFileName = ref("");
const prizesFileName = ref("");
const idList = ref([]);
const rawPrizeList = ref([]);
const startingSN = ref(1);
const winnersList = ref([]);

onMounted(async () => {
  try {
    existingDraws.value = await fetchPreDraws();
  } catch {
    /* fall back to empty list */
  }
});

const luckyDrawLink = computed(() => luckyDrawPickerUrl(drawDid.value));
const exportUrl = computed(() => preDrawExportUrl(drawDid.value));

async function parseFile(file) {
  const buffer = await file.arrayBuffer();
  try {
    const { read, utils } = await import("xlsx");
    const wb = read(buffer);
    return utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
  } catch {
    const text = new TextDecoder().decode(buffer);
    const lines = text.trim().split(/\r?\n/);
    const headers = lines[0].split(",").map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const values = line.split(",");
      return Object.fromEntries(headers.map((h, i) => [h, (values[i] ?? "").trim()]));
    });
  }
}

async function handleParticipantsFile(e) {
  const file = e.target.files?.[0];
  e.target.value = "";
  if (!file) return;
  participantsFileName.value = file.name;
  idList.value = await parseFile(file);
}

async function handlePrizesFile(e) {
  const file = e.target.files?.[0];
  e.target.value = "";
  if (!file) return;
  prizesFileName.value = file.name;
  rawPrizeList.value = await parseFile(file);
}

function goToDrawWinners() {
  const selected = existingDraws.value.find((d) => d.id === drawDid.value);
  winnersList.value = selected?.preDrawWinners || [];
  step.value = 3;
}

async function setupDraw() {
  error.value = "";
  if (drawSelection.value === "new" && !drawName.value) {
    error.value = "Please include the draw name.";
    return;
  }
  if (drawSelection.value === "existing" && !drawDid.value) {
    error.value = "Please select an existing draw.";
    return;
  }
  if (drawSelection.value === "existing") {
    if (!window.confirm("Are you sure you want to reset all previous draw results?")) return;
    loading.value = true;
    try {
      await resetLuckyDraw(drawDid.value);
    } catch {
      error.value = "Error occurred. Please try again.";
      return;
    } finally {
      loading.value = false;
    }
  }
  step.value = 2;
}

async function submitDraw() {
  error.value = "";
  if (idList.value.length === 0) {
    error.value = "Please include the participant list.";
    return;
  }
  if (rawPrizeList.value.length === 0) {
    error.value = "Please include the prize list.";
    return;
  }
  const filteredPrizes = rawPrizeList.value.filter(
    (row) => row.Prize !== undefined && row.Prize !== null && row.Prize !== "",
  );
  loading.value = true;
  try {
    const data = await uploadPreDraw({
      idList: idList.value,
      rawPrizeList: filteredPrizes,
      startingSN: startingSN.value,
      docID: drawDid.value,
      alias: drawName.value,
    });
    winnersList.value = data.winnersList || [];
    drawDid.value = data.did;
    step.value = 3;
  } catch {
    error.value = "Error occurred. Please try again.";
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <section>
    <h1 class="text-h5 mb-4">Lucky Draw - Pre Draw</h1>

    <div class="d-flex align-center mb-4" style="gap: 8px">
      <VChip :color="step === 1 ? 'primary' : undefined" :variant="step === 1 ? 'flat' : 'outlined'">1. New or Existing</VChip>
      <VIcon icon="ri-arrow-right-s-line" />
      <VChip :color="step === 2 ? 'primary' : undefined" :variant="step === 2 ? 'flat' : 'outlined'">2. Set up Draw</VChip>
      <VIcon icon="ri-arrow-right-s-line" />
      <VChip :color="step === 3 ? 'primary' : undefined" :variant="step === 3 ? 'flat' : 'outlined'">3. Draw Winners</VChip>
    </div>

    <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>

    <VCard v-if="step === 1" class="pa-4" max-width="560">
      <h3 class="mb-4">New or Existing Draw</h3>
      <VRadioGroup v-model="drawSelection" inline hide-details>
        <VRadio label="New Draw" value="new" />
        <VRadio label="Existing Draw" value="existing" />
      </VRadioGroup>

      <VTextField
        v-if="drawSelection === 'new'"
        v-model="drawName"
        label="Draw Name"
        placeholder="Enter draw name"
        class="mt-4"
      />

      <div v-else class="mt-4">
        <VSelect
          v-model="drawDid"
          :items="existingDraws"
          item-title="alias"
          item-value="id"
          label="Select Existing Draw"
        />
        <div v-if="drawDid" class="mt-2">
          <p class="mb-2">Link to lucky draw: {{ luckyDrawLink }}</p>
          <VBtn color="secondary" @click="goToDrawWinners">Go to Draw Winners Record</VBtn>
        </div>
      </div>

      <VBtn color="primary" class="mt-4" :loading="loading" @click="setupDraw">Set up draw</VBtn>
    </VCard>

    <VCard v-else-if="step === 2" class="pa-4" max-width="560">
      <h3 class="mb-2">Step 1: Upload the list of Participants</h3>
      <p class="text-caption text-medium-emphasis mb-2">Headers: Name, Employee ID</p>
      <input type="file" accept=".csv,.xlsx" :disabled="loading" @change="handleParticipantsFile" />
      <p v-if="participantsFileName" class="text-caption text-medium-emphasis mt-1">
        {{ participantsFileName }} — {{ idList.length }} participant(s)
      </p>

      <h3 class="mb-2 mt-6">Step 2: Upload the list of Prizes</h3>
      <p class="text-caption text-medium-emphasis mb-2">Headers: Prize, Qty</p>
      <input type="file" accept=".csv,.xlsx" :disabled="loading" @change="handlePrizesFile" />
      <p v-if="prizesFileName" class="text-caption text-medium-emphasis mt-1">
        {{ prizesFileName }} — {{ rawPrizeList.length }} prize row(s)
      </p>

      <h3 class="mb-2 mt-6">Step 3: Starting S/N of prize list to predraw</h3>
      <VTextField v-model.number="startingSN" type="number" label="Starting S/N" style="max-width: 200px" />

      <VBtn color="primary" class="mt-4" :loading="loading" @click="submitDraw">Submit</VBtn>
    </VCard>

    <VCard v-else class="pa-4">
      <div class="d-flex mb-4" style="gap: 8px">
        <VBtn color="primary" :href="luckyDrawLink" target="_blank">Go to Lucky Draw</VBtn>
        <VBtn color="secondary" :href="exportUrl">Export</VBtn>
      </div>
      <h3 class="mb-2">Winners</h3>
      <VTable density="compact">
        <thead>
          <tr>
            <th>S/N</th>
            <th>Name</th>
            <th>Staff ID</th>
            <th>Prize</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(winner, i) in winnersList" :key="i">
            <td>{{ winner.sn }}</td>
            <td>{{ winner.name }}</td>
            <td>{{ winner.id }}</td>
            <td>{{ winner.prize }}</td>
          </tr>
        </tbody>
      </VTable>
    </VCard>
  </section>
</template>
